//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import {
  getYouTubeAudienceAnalytics,
  getYouTubeEngagementAnalytics,
  getYouTubeUser,
  getYouTubeVideos,
} from '@apis/youtube/youtubeApi';
import {
  YouTubeAudienceMetrics,
  YouTubeEngagementDailyAndSummaryMetrics,
  YouTubeMetricSortKey,
  YouTubeUser,
} from '@apis/youtube/youtubeApiModel';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { YouTubeChannelId } from '@models/YouTubeChannelId';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined } from '@utils/filterPredicates';
import { YOUTUBE_API_DATE_FORMAT } from '@youtube/components/analytics/audience/youtubeAudienceConstants';
import {
  YouTubeEmptyAudienceMetrics,
  YouTubeEmptyEngagementMetrics,
} from '@youtube/youtubeConstants';
import { fillInMissingEngagementDates } from '@youtube/youtubeUtils';

const getYouTubeChannelId = (data: YouTubeUser) => data.channelId;

export const useGetYouTubeChannelIdQuery = () =>
  useGetYouTubeUserQuery(getYouTubeChannelId);

export function useGetYouTubeUserQuery<TData = YouTubeUser>(
  select?: (data: YouTubeUser) => TData,
) {
  return useQuery({
    queryKey: [QueryKeyConstants.YouTube, QueryKeyConstants.YouTubeUser],
    queryFn: async ({ signal }) => {
      return getYouTubeUser({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}

interface GetYouTubeAudienceAnalyticsQueryParams {
  channelId: YouTubeChannelId | undefined;
  dateRange: IAliasedDateRange;
  timezone: TimeZoneId;
}

export const useGetYouTubeAudienceAnalyticsQuery = <
  TData = YouTubeAudienceMetrics,
>(
  { channelId, dateRange, timezone }: GetYouTubeAudienceAnalyticsQueryParams,
  select?: (data: YouTubeAudienceMetrics) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.YouTube,
      QueryKeyConstants.YouTubeAudience,
      { channelId, dateRange, timezone },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(channelId)) {
        throw new Error('channelId was undefined');
      }

      const res = await getYouTubeAudienceAnalytics(
        {
          youtubeChannelId: channelId,
          startTime: DateString.toDate(dateRange.startDate, timezone),
          endTime: DateString.toDate(dateRange.endDate, timezone),
        },
        { signal },
      );

      const dailyMetrics = Object.entries(res.dailyMetrics).map(
        ([date, metrics]) => ({
          date: DateString.fromString(date, YOUTUBE_API_DATE_FORMAT),
          metrics,
        }),
      );

      return {
        ...res,
        dailyMetrics: fillInMissingEngagementDates({
          days: dailyMetrics,
          emptyMetrics: YouTubeEmptyAudienceMetrics,
          end: dateRange.endDate,
          start: dateRange.startDate,
          timezone,
        }),
      };
    },
    enabled: isDefined(channelId),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

interface GetYouTubeEngagementAnalyticsQueryParams {
  channelId: YouTubeChannelId | undefined;
  dateRange: IAliasedDateRange;
  timezone: TimeZoneId;
  username: string | undefined;
}

export const useGetYouTubeEngagementAnalyticsQuery = <
  TData = YouTubeEngagementDailyAndSummaryMetrics,
>(
  {
    channelId,
    dateRange,
    timezone,
    username,
  }: GetYouTubeEngagementAnalyticsQueryParams,
  select?: (data: YouTubeEngagementDailyAndSummaryMetrics) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.YouTube,
      QueryKeyConstants.YouTubeEngagement,
      {
        channelId,
        dateRange,
        timezone,
        username,
      },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(channelId) || !isDefined(username)) {
        throw new Error('channelId or username were undefined');
      }

      const res = await getYouTubeEngagementAnalytics(
        {
          youtubeChannelId: channelId,
          username,
          startTime: DateString.toDate(dateRange.startDate, timezone),
          endTime: DateString.toDate(dateRange.endDate, timezone),
        },
        { signal },
      );

      const dailyMetrics = res.dailyMetrics.map(({ date, metrics }) => ({
        date: DateString.fromTimestamp(date, timezone),
        metrics,
      }));

      return {
        ...res,
        dailyMetrics: fillInMissingEngagementDates({
          days: dailyMetrics,
          emptyMetrics: YouTubeEmptyEngagementMetrics,
          end: dateRange.endDate,
          start: dateRange.startDate,
          timezone,
        }),
      };
    },
    enabled: isDefined(channelId) && isDefined(username),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

interface GetYouTubeVideosQueryParams {
  channelId: YouTubeChannelId | undefined;
  dateRange: IAliasedDateRange;
  isOrderAscending?: boolean;
  limit?: number;
  offset?: number;
  sortKey?: YouTubeMetricSortKey;
  timezone: TimeZoneId;
  username: string | undefined;
}

export const useGetYouTubeVideosQuery = ({
  channelId,
  dateRange,
  isOrderAscending,
  limit,
  offset,
  sortKey,
  timezone,
  username,
}: GetYouTubeVideosQueryParams) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.YouTube,
      QueryKeyConstants.YouTubeVideos,
      {
        channelId,
        dateRange,
        isOrderAscending,
        limit,
        offset,
        sortKey,
        timezone,
        username,
      },
    ],
    queryFn: async ({ signal }) => {
      if (!isDefined(channelId) || !isDefined(username)) {
        throw new Error('channelId or username were undefined');
      }

      return getYouTubeVideos(
        {
          channelId,
          username,
          startTime: DateString.toDate(dateRange.startDate, timezone),
          endTime: DateString.toDate(dateRange.endDate, timezone),
          limit,
          offset,
          sortKey,
          isOrderAscending,
        },
        { signal },
      );
    },
    enabled: isDefined(channelId) && isDefined(username),
    keepPreviousData: true,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
